import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, FieldProps, Form, Formik, FormikProps } from 'formik';
import styled, { css } from 'styled-components';
import * as Yup from 'yup';
import { Button, CentBasedInputCurrency, ErrorText, FieldWrapper, Modal, ModalFooter, ModalHeader, ModalTitle } from 'components';
import { Text } from 'components/Typography';
import { lineHeight, theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { selectSiteSettings } from 'store/Site';
import { selectCalculatorChangeLog, setCalculatorPayees, setChangeLogEntries } from 'store/TipCalculator';
import { ITipCalculatorPayee } from 'types';
import { buildCashPayoutEnteredChangeLogEntries, calculateHoldbackAfterCash, centsToDollarsDisplay } from 'utils';

const gridStyles = css`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${toRem(theme.space.sm)};
  width: 100%;
`;

const ModalSubTitle = styled(Text)`
  margin-top: ${toRem(theme.space.lg)};
  font-size: ${toRem(theme.font.size.lg)};
  line-height: ${lineHeight(theme.font.size.lg)};
  color: ${theme.color.neutral[500]};
`;

const CashPayeesFormContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  background-color: ${theme.color.neutral[50]};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${theme.borderRadius.small}px;
  margin-top: ${toRem(theme.space.xl)};
  padding: ${toRem(theme.space.xl)};
  width: 100%;
`;

const Header = styled.header`
  ${gridStyles}
  margin-bottom: ${toRem(theme.space.sm)};
`;

const GridLabel = styled.p`
  font-size: ${toRem(theme.font.size.xs)};
  font-weight: ${toRem(theme.font.weight.regular)};
  color: ${theme.color.neutral[500]};
`;

const PayeesList = styled.ul`
  list-style: none;
`;

const PayeeListItem = styled.li`
  ${gridStyles}
  align-items: center;
  margin-bottom: ${toRem(theme.space.sm)};

  &.total-cash {
    .total-cash-label {
      grid-column: 2 / 3;
      text-align: right;
    }

    .total-cash-value {
      grid-column: 3 / 4;

      input:read-only {
        padding: 0 ${toRem(theme.space.md)};
      }
    }

    .total-cash-error {
      grid-column: 3 / 4;
    }
  }
`;

const noop = () => {};

interface ICashPayoutModalProps {
  dataCy?: string;
  hideModal: () => void;
  payees: ITipCalculatorPayee[];
}

interface ICashForm {
  payees: ITipCalculatorPayee[];
}

const getErrorMessage = (maxGross: number, withholding: number) =>
  withholding > 0 ? `Max cash payout is ${centsToDollarsDisplay(maxGross)}\n${centsToDollarsDisplay(withholding)} reserved for payroll withholdings` : `Cannot exceed ${centsToDollarsDisplay(maxGross)}`;

const CashPayoutModal: React.FC<ICashPayoutModalProps> = ({ dataCy = 'cash-payout-modal', hideModal, payees }) => {
  const dispatch = useDispatch();
  const formRef = useRef<FormikProps<ICashForm>>(null);

  // Change log entries
  let changeLogEntries = useSelector(selectCalculatorChangeLog());

  // Site holdback settings
  const { holdback: currentSiteHoldbacks } = useSelector(selectSiteSettings()) || {};

  useEffect(() => {
    // when there has been a recalculation, we want to validate any existing values on form load
    if (formRef.current) formRef.current.validateForm();
  }, []);

  const handleSubmit = (values: ICashForm) => {
    const updatedPayees = values.payees.map((payee) => {
      // Build change log entries
      changeLogEntries = buildCashPayoutEnteredChangeLogEntries({ payee, value: payee.cash ?? 0 }, changeLogEntries);

      const holdbackAmount = calculateHoldbackAfterCash(payee, currentSiteHoldbacks, changeLogEntries);

      return {
        ...payee,
        net: payee.gross - holdbackAmount,
        holdbackAmount,
      };
    });

    // Update the change log entries
    dispatch(setChangeLogEntries(changeLogEntries));

    // Update the payees
    dispatch(setCalculatorPayees(updatedPayees));

    // Hide the modal
    hideModal();
  };

  const validationSchema = Yup.object().shape({
    payees: Yup.array().of(
      Yup.object().shape({
        cash: Yup.number()
          .default(undefined)
          .test({
            name: 'maxCash',
            test: (value, ctx) => {
              const withheldGross = ctx.parent.gross - (ctx.parent.withholdingAmount || 0);
              return !value || value <= withheldGross || ctx.createError({ message: getErrorMessage(withheldGross, ctx.parent.withholdingAmount) });
            },
          }),
      })
    ),
  });

  const getCashTotal = (values: ICashForm) => values.payees.reduce((acc, payee) => acc + (payee.cash || 0), 0);

  return (
    <Modal dataCy={dataCy} maxWidth={680} isActive onClose={hideModal} hideCloseButton closeOnOutSideClick={false}>
      <ModalHeader data-cy={`${dataCy}-header`}>
        <ModalTitle>Cash Payouts</ModalTitle>
        <ModalSubTitle>Enter cash that has been disbursed to employees.</ModalSubTitle>
      </ModalHeader>
      <Formik innerRef={formRef} initialValues={{ payees }} onSubmit={handleSubmit} validationSchema={validationSchema} mapPropsToErrors={{ payees }} enableReinitialize>
        {({ setFieldValue, values, isSubmitting, dirty, isValid }) => {
          const cashTotal = getCashTotal(values);
          return (
            <Form data-cy={`${dataCy}-form`}>
              <CashPayeesFormContainer>
                <Header>
                  <GridLabel>Employee Name</GridLabel>
                  <GridLabel>Calculated Tips</GridLabel>
                  <GridLabel>Cash Amount</GridLabel>
                </Header>
                <PayeesList data-cy={`${dataCy}-list`}>
                  {values.payees?.map((payee, idx) => (
                    <Field key={`${payee.id}-${idx}`} name={`payees[${idx}].cash`}>
                      {({ field, meta }: FieldProps) => (
                        <PayeeListItem data-cy={`${dataCy}-list-item`}>
                          <Text size="xsmall" fontWeight="medium" allowWordWrap>
                            {payee.name}
                          </Text>
                          <Text size="xsmall" fontWeight="medium">
                            {centsToDollarsDisplay(payee.gross)}
                          </Text>
                          <FieldWrapper message={meta.error ? meta.error : ''} hasError={!!meta.error}>
                            <CentBasedInputCurrency
                              {...field}
                              data-cy="cash-input"
                              value={field.value || ''}
                              hasError={!!meta.error}
                              placeholder={payee.withholdingAmount > 0 ? `${centsToDollarsDisplay(payee.gross - payee.withholdingAmount)} max` : '$00.00'}
                              allowZero
                              onChange={(value) => setFieldValue(`payees[${idx}].cash`, parseInt(value, 10))}
                            />
                          </FieldWrapper>
                        </PayeeListItem>
                      )}
                    </Field>
                  ))}
                  <PayeeListItem className="total-cash" data-cy={`${dataCy}-list-total`}>
                    <GridLabel className="total-cash-label">Total Cash</GridLabel>
                    <>
                      <CentBasedInputCurrency className="total-cash-value" readOnly allowZero value={centsToDollarsDisplay(cashTotal)} onChange={noop} />
                      {dirty && cashTotal < 0 && (
                        <ErrorText className="total-cash-error" size="xxsmall" data-cy="cash-payouts-total-eror">
                          Must be more than $00.00
                        </ErrorText>
                      )}
                    </>
                  </PayeeListItem>
                </PayeesList>
              </CashPayeesFormContainer>
              <ModalFooter columns={2}>
                <Button label="Cancel" appearance={Appearance.WHITE} onClick={hideModal} data-cy={`${dataCy}-cancel-button`} />
                <Button label="Save" type="submit" appearance={Appearance.PRIMARY} isProcessing={isSubmitting} disabled={isSubmitting || !isValid || (dirty && cashTotal < 0) || !dirty} data-cy={`${dataCy}-save-button`} />
              </ModalFooter>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default CashPayoutModal;
